import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const WebhooksSection = () => {
  const [selectedEvent, setSelectedEvent] = useState('policy.updated');

  const events = [
    {
      id: 'policy.updated',
      name: 'policy.updated',
      description: 'Triggered when a policy is modified or updated',
      frequency: 'Real-time',
      payload: `{
  "event": "policy.updated",
  "timestamp": "2026-01-28T03:02:31Z",
  "data": {
    "policy_id": "pol_abc123",
    "title": "Cardiovascular Device Coverage Policy",
    "payer": "Anthem Blue Cross",
    "category": "cardiovascular",
    "changes": [
      {
        "field": "coverage_criteria",
        "old_value": "Age 18-65",
        "new_value": "Age 18-75",
        "change_type": "criteria_update"
      }
    ],
    "effective_date": "2026-02-01",
    "impact_level": "high"
  }
}`
    },
    {
      id: 'policy.created',
      name: 'policy.created',
      description: 'Triggered when a new policy is added to the system',
      frequency: 'Real-time',
      payload: `{
  "event": "policy.created",
  "timestamp": "2026-01-28T03:02:31Z",
  "data": {
    "policy_id": "pol_xyz789",
    "title": "New Orthopedic Device Policy",
    "payer": "UnitedHealthcare",
    "category": "orthopedic",
    "status": "active",
    "effective_date": "2026-02-15",
    "coverage_criteria": "Detailed criteria...",
    "documentation_requirements": ["Prior auth", "Clinical notes"]
  }
}`
    },
    {
      id: 'policy.archived',
      name: 'policy.archived',
      description: 'Triggered when a policy is archived or deprecated',
      frequency: 'Real-time',
      payload: `{
  "event": "policy.archived",
  "timestamp": "2026-01-28T03:02:31Z",
  "data": {
    "policy_id": "pol_old456",
    "title": "Legacy Device Coverage Policy",
    "payer": "Aetna",
    "archived_date": "2026-01-28",
    "reason": "Replaced by updated policy",
    "replacement_policy_id": "pol_new789"
  }
}`
    },
    {
      id: 'payer.updated',
      name: 'payer.updated',
      description: 'Triggered when payer information is modified',
      frequency: 'Daily',
      payload: `{
  "event": "payer.updated",
  "timestamp": "2026-01-28T03:02:31Z",
  "data": {
    "payer_id": "payer_123",
    "name": "Anthem Blue Cross",
    "changes": [
      {
        "field": "contact_email",
        "old_value": "old@anthem.com",
        "new_value": "new@anthem.com"
      }
    ]
  }
}`
    },
    {
      id: 'alert.critical',
      name: 'alert.critical',
      description: 'Triggered for critical policy changes requiring immediate attention',
      frequency: 'Real-time',
      payload: `{
  "event": "alert.critical",
  "timestamp": "2026-01-28T03:02:31Z",
  "data": {
    "alert_id": "alert_urgent123",
    "severity": "critical",
    "title": "Immediate Coverage Change",
    "description": "Major payer has discontinued coverage for specific device category",
    "affected_policies": ["pol_abc123", "pol_def456"],
    "action_required": true,
    "deadline": "2026-02-01"
  }
}`
    }
  ];

  const selectedEventData = events?.find(e => e?.id === selectedEvent);

  return (
    <section className="py-12 md:py-16 lg:py-20 px-4 md:px-6 lg:px-8 bg-gray-50">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
            Webhooks & Real-Time Events
          </h2>
          <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
            Receive instant notifications when policies change. Configure webhooks to keep your systems synchronized with the latest policy updates.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8 mb-12">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
              <Icon name="Zap" size={24} color="var(--color-secondary)" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Real-Time Delivery</h3>
            <p className="text-sm text-gray-600">
              Events delivered within milliseconds of policy changes with guaranteed delivery and automatic retries.
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
              <Icon name="Shield" size={24} color="var(--color-success)" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Secure & Verified</h3>
            <p className="text-sm text-gray-600">
              HMAC signature verification ensures webhook authenticity and prevents unauthorized access.
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
              <Icon name="Filter" size={24} color="#9333EA" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Smart Filtering</h3>
            <p className="text-sm text-gray-600">
              Subscribe to specific event types and filter by category, payer, or custom criteria.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <h3 className="text-base font-semibold text-gray-900 mb-4">Event Types</h3>
              <div className="space-y-2">
                {events?.map((event) => (
                  <button
                    key={event?.id}
                    onClick={() => setSelectedEvent(event?.id)}
                    className={`w-full text-left px-3 py-3 rounded-lg transition-colors ${
                      selectedEvent === event?.id
                        ? 'bg-blue-50 border-2 border-blue-600' :'border border-gray-200 hover:border-blue-300'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <code className="text-xs md:text-sm font-mono text-blue-600 break-all">
                        {event?.name}
                      </code>
                      <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded whitespace-nowrap">
                        {event?.frequency}
                      </span>
                    </div>
                    <p className="text-xs text-gray-600 mt-2">{event?.description}</p>
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-6 bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <h3 className="text-base font-semibold text-gray-900 mb-4">Webhook Setup</h3>
              <div className="space-y-3 text-sm text-gray-700">
                <div className="flex items-start gap-2">
                  <Icon name="CheckCircle2" size={16} color="var(--color-success)" className="flex-shrink-0 mt-0.5" />
                  <span>Configure endpoint URL</span>
                </div>
                <div className="flex items-start gap-2">
                  <Icon name="CheckCircle2" size={16} color="var(--color-success)" className="flex-shrink-0 mt-0.5" />
                  <span>Select event types</span>
                </div>
                <div className="flex items-start gap-2">
                  <Icon name="CheckCircle2" size={16} color="var(--color-success)" className="flex-shrink-0 mt-0.5" />
                  <span>Add optional filters</span>
                </div>
                <div className="flex items-start gap-2">
                  <Icon name="CheckCircle2" size={16} color="var(--color-success)" className="flex-shrink-0 mt-0.5" />
                  <span>Verify signature</span>
                </div>
              </div>
              <Button variant="default" size="sm" fullWidth className="mt-4" iconName="Plus" iconPosition="left">
                Create Webhook
              </Button>
            </div>
          </div>

          <div className="lg:col-span-2">
            {selectedEventData && (
              <div className="space-y-6">
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 md:p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-lg md:text-xl font-semibold text-gray-900 mb-2">
                        {selectedEventData?.name}
                      </h3>
                      <p className="text-sm md:text-base text-gray-600">
                        {selectedEventData?.description}
                      </p>
                    </div>
                    <span className="text-xs px-3 py-1 bg-green-100 text-green-700 rounded-full font-medium whitespace-nowrap">
                      {selectedEventData?.frequency}
                    </span>
                  </div>

                  <div className="bg-gray-900 rounded-lg overflow-hidden">
                    <div className="flex items-center justify-between px-4 py-3 bg-gray-800 border-b border-gray-700">
                      <span className="text-gray-400 text-xs md:text-sm">Webhook Payload</span>
                      <Button variant="ghost" size="xs" iconName="Copy">
                        Copy
                      </Button>
                    </div>
                    <div className="p-4 md:p-6 overflow-x-auto">
                      <pre className="text-green-400 text-xs md:text-sm">
                        <code>{selectedEventData?.payload}</code>
                      </pre>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 md:p-6">
                  <h4 className="text-base md:text-lg font-semibold text-gray-900 mb-4">Signature Verification</h4>
                  <p className="text-sm text-gray-600 mb-4">
                    Verify webhook authenticity using HMAC-SHA256 signature in the X-PolicyAlerts-Signature header.
                  </p>
                  <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                    <pre className="text-green-400 text-xs md:text-sm">
                      <code>{`const crypto = require('crypto');

function verifyWebhook(payload, signature, secret) {
  const hmac = crypto.createHmac('sha256', secret);
  const digest = hmac.update(payload).digest('hex');
  return crypto.timingSafeEqual(
    Buffer.from(signature),
    Buffer.from(digest)
  );
}

// Usage in your webhook handler
app.post('/webhooks/policy-alerts', (req, res) => {
  const signature = req.headers['x-policyalerts-signature'];
  const isValid = verifyWebhook(
    JSON.stringify(req.body),
    signature,
    process.env.WEBHOOK_SECRET
  );
  
  if (!isValid) {
    return res.status(401).send('Invalid signature');
  }
  
  // Process webhook event
  console.log('Event:', req.body.event);
  res.status(200).send('OK');
});`}</code>
                    </pre>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default WebhooksSection;